import { Inject, Injectable } from '@angular/core';

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigService } from './config.service';
import {BasicResponse, enumUserStatus, PortalMultiFactor, PortalRegister} from '../..';
import { User, UserMultiFactorEnrollment } from '../..';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { SortFilterInterface } from '../model/sort-filter';

export interface UsersAPI {
  items: User[];
  total_count: number;
}

export interface UserExistAPI {
  userExist: boolean;
}

export interface EmailInUseAPI {
  emailInUse: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private URL;
  headers: HttpHeaders;

  constructor(@Inject(ConfigService) private config, private httpClient: HttpClient) {
    this.URL = config.REST_SERVER_HOST + config.REST_SERVER_url.find(restUrl => restUrl.api === 'PADM').url;
    this.headers = new HttpHeaders().set('DoNotIntercept', 'true');
  }

  private static handleHTTPError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
          `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    // return an observable with a user-facing error message
    // return throwError('Something bad happened; please try again later.');
    return throwError(error);
  }

  getUsers(pageIndex: number, pageSize: number, sortActive: string, sort: string, sortFilter: SortFilterInterface): Observable<UsersAPI> {

    const headers = this.headers;
    if ((sortActive === '') || (sort === '')) {
      sortActive = '0';
      sort = 'asc';
    }
    return this.httpClient.post<UsersAPI>(
        `${this.URL}/accesscontrol/user/${pageIndex}/${pageSize}/${sortActive}/${sort}`,sortFilter, {headers}).pipe(
        catchError(UserService.handleHTTPError)
    );

  }

  getUser(id: number): Observable<User> {
    const headers = this.headers;
    return this.httpClient.get<User>(
        `${this.URL}/accesscontrol/user/${id}`, {headers}).pipe(
        catchError(UserService.handleHTTPError)
    );

  }

  updateUser(userObj: User): Observable<BasicResponse> {
    const headers = this.headers;
    const id = userObj.id;
    const { id: _omit, ...body } = userObj;
    return this.httpClient.patch<BasicResponse>(`${this.URL}/accesscontrol/user/${id}`,
        body, {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  createUser(userObj: User): Observable<BasicResponse> {
    const obj = {
      cellphoneNo: userObj.cellphoneNo,
      firstName: userObj.firstName,
      lastName: userObj.lastName,
      identificationNo: userObj.identificationNo,
      userName: userObj.userName,
      emailAddress: userObj.emailAddress,
      status: userObj.status
    };
    const headers = this.headers;
    return this.httpClient.put<BasicResponse>(`${this.URL}/accesscontrol/user/`,
        obj, {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  getUserAvailablePortals(uacId: number): Observable<PortalRegister[]> {
    const headers = this.headers;
    return this.httpClient.get<PortalRegister[]>(`${this.URL}/accesscontrol/user/useravailportals/${uacId}`,
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  getUserMultiFactorEnrollments(uacId: number): Observable<UserMultiFactorEnrollment[]> {
    const headers = this.headers;
    return this.httpClient.get<UserMultiFactorEnrollment[]>(`${this.URL}/accesscontrol/user/${uacId}/enrollment`,
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  getUserAvailableMultiFactors(uacId: number): Observable<PortalMultiFactor[]> {
    const headers = this.headers;
    return this.httpClient.get<PortalMultiFactor[]>(`${this.URL}/accesscontrol/user/${uacId}/multifactors`,
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  usernameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return this.checkIfUsernameExists(control.value).pipe(
          map(res => {
            return res.userExist ? {usernameExists: true} : null;
            // NB: Return null if there is no error
          })
      );
    };
  }

  checkIfUsernameExists(username: string): Observable<UserExistAPI> {
    const headers = this.headers;
    return this.httpClient.get<UserExistAPI>(`${this.URL}/accesscontrol/user/userexist/${username}`,
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  emailValidator(uacId: number): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return this.checkIfEmailInUse(control.value, uacId).pipe(
          map(res => {
            return res.emailInUse ? {emailAddressInUse: true} : null;
            // NB: Return null if there is no error
          })
      );
    };
  }

  checkIfEmailInUse(email: string, uacId: number):Observable<EmailInUseAPI> {
    console.log('email', email);
    if (!uacId) {
      uacId = 0;
      console.log('uacid', uacId);
    }
    const headers = this.headers;
    return this.httpClient.get<EmailInUseAPI>(`${this.URL}/accesscontrol/user/emailinuse/${email}/${uacId}`,
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  deleteUserMultiFactorEnrollment(uacID: number, enrollmentId: string): Observable<BasicResponse>
  {
    const headers = this.headers;
    return this.httpClient.delete<BasicResponse>(`${this.URL}/multifactor/${uacID}/enrollment/${enrollmentId}`,
         {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  enrollMultiFactor(uacID: number, portalID: number): Observable<BasicResponse>
  {
    const headers = this.headers;
    return this.httpClient.post<BasicResponse>(`${this.URL}/multifactor/${uacID}/${portalID}/enrollment/ticket`,'',
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

  resetPassword(uacID: number, portalID: number): Observable<BasicResponse>
  {
    const headers = this.headers;
    return this.httpClient.patch<BasicResponse>(`${this.URL}/accesscontrol/user/${uacID}/resetpassword/portal/${portalID}`,'',
        {headers})
        .pipe(
            catchError(UserService.handleHTTPError)
        );
  }

    changeStatus(uacID: number, newStatus: enumUserStatus, portalId: number) {
      const headers = this.headers;
      return this.httpClient.patch<BasicResponse>(`${this.URL}/accesscontrol/user/${uacID}/portalId/${portalId}/status/${newStatus}`,'',
          {headers})
          .pipe(
              catchError(UserService.handleHTTPError)
          );
    }

    notify(uacID: number, portalID: number): Observable<BasicResponse> {
        const headers = this.headers;
        return this.httpClient.patch<BasicResponse>(`${this.URL}/accesscontrol/user/${uacID}/welcomenotify/portal/${portalID}`,'',
            {headers})
            .pipe(
                catchError(UserService.handleHTTPError)
            );
    }
}

// ========================================
// CHANGE LOG
// ========================================
// C012: Remove id from update user request body. What changed: In updateUser(), the request body sent in the PATCH now omits the id property: we destructure id from userObj for the URL path and send only the rest as body. Previous behavior: PATCH url/user/${userObj.id} with full userObj (including id) as body. New behavior: PATCH url/user/${id} with body = { ...userObj without id }. Reason: User request to remove id from the update user JSON payload; id is only needed in the URL. Impact: Backend update API receives request body without id. - 2026-01-29 - HE
// See C013 - 2026-03-30 - HE: PADM string-field coercion for create/update user was moved out of UserService; portaladmin-only normalization lives in apps/portaladmin (padm-user-payload.util.ts + user-container). Previous behavior (reverted): UserService.createUser/updateUser applied padmUserTextField to all consumers. New behavior: Shared service sends payloads as before; only portaladmin container normalizes before calling UserService. Reason: User requested fixes only for apps/portaladmin. Impact: portaladmin-cx, portaladmin-ss, etc. unchanged from pre-C013 service behavior. - 2026-03-30 - HE
